import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import GridViewIcon from '@mui/icons-material/GridView';
import LayersIcon from '@mui/icons-material/Layers';

import TypographyPanel from './TypographyPanel';
import SizePanel from './SizePanel';
import LayoutPanel from './LayoutPanel';
import BoxPanel from './BoxPanel';
import PagePanel from './PagePanel';

const Section = ({ icon, label, defaultExpanded = true, children }) => (
  <Accordion
    disableGutters
    elevation={0}
    defaultExpanded={defaultExpanded}
    sx={{
      border: 'none',
      '&:before': { display: 'none' },
      '& .MuiAccordionSummary-root': { minHeight: 36, px: 1.5 },
      '& .MuiAccordionDetails-root': { px: 1.5, pb: 1.5, pt: 0 },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {React.cloneElement(icon, { sx: { fontSize: 13, color: '#888' } })}
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: '#555' }}>
          {label}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const PropertyPanel = ({
  selectedElement,
  onClose,
  onStyleChange,
}) => {
  const styles = selectedElement?.styles || {};
  const hasElement = !!selectedElement;

  const handleChange = (property, value) => {
    if (onStyleChange) onStyleChange(property, value);
  };

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          borderBottom: '1px solid #eee',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: '#555', flex: 1 }}>
          {hasElement ? (selectedElement.tagName || 'Element') : 'Page'}
        </Typography>
        {hasElement && (
          <Typography variant="caption" sx={{ fontSize: 10, color: '#aaa', fontFamily: 'monospace' }}>
            {selectedElement.elementId}
          </Typography>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: '#aaa' }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Panels */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {!hasElement ? (
          <Box sx={{ p: 1.5 }}>
            <PagePanel styles={styles} onChange={handleChange} />
          </Box>
        ) : (
          <>
            <Section icon={<LayersIcon />} label="Box" defaultExpanded>
              <BoxPanel styles={styles} onChange={handleChange} />
            </Section>
            <Divider />
            <Section icon={<TextFieldsIcon />} label="Typography">
              <TypographyPanel styles={styles} onChange={handleChange} />
            </Section>
            <Divider />
            <Section icon={<GridViewIcon />} label="Layout">
              <LayoutPanel styles={styles} onChange={handleChange} />
            </Section>
            <Divider />
            <Section icon={<AspectRatioIcon />} label="Size">
              <SizePanel styles={styles} onChange={handleChange} />
            </Section>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PropertyPanel;
